import React, { useState } from 'react';
import {
  Target, TrendingUp, BookOpen, Award, Clock, Users,
  CheckCircle, Star, Brain, Code, Palette, Database,
  Shield, Briefcase, BarChart3, Zap, Play, Plus,
  Search, Filter, Grid3X3, List, Eye, Heart,
  Share2, Bookmark, Trophy, Layers, Settings,
  ChevronRight, ArrowRight, Calendar, User,
  LineChart, PieChart, Activity, Sparkles
} from 'lucide-react';

interface Skill {
  id: string;
  name: string;
  category: string;
  level: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  progress: number;
  totalCourses: number;
  completedCourses: number;
  avgRating: number;
  studyTime: number; // in hours
  lastPracticed: string;
  trending: boolean;
  inDemand: boolean;
  estimatedLearningTime: string;
  prerequisites?: string[];
  relatedSkills: string[];
  certificationAvailable: boolean;
  marketDemand: 'low' | 'medium' | 'high' | 'very-high';
}

interface SkillCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  skillCount: number;
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  skills: string[];
  duration: string;
  difficulty: string;
  completionRate: number;
}

export default function Skills() {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'progress' | 'name' | 'demand' | 'trending'>('progress');
  const [showAssessment, setShowAssessment] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);

  const categories: SkillCategory[] = [
    {
      id: 'all',
      name: 'All Skills',
      description: 'View all your skills',
      icon: <Target className="w-5 h-5" />,
      color: 'from-gray-500 to-gray-600',
      skillCount: 45
    },
    {
      id: 'programming',
      name: 'Programming',
      description: 'Software development and coding',
      icon: <Code className="w-5 h-5" />,
      color: 'from-blue-500 to-indigo-600',
      skillCount: 18
    },
    {
      id: 'design',
      name: 'Design',
      description: 'UI/UX and visual design',
      icon: <Palette className="w-5 h-5" />,
      color: 'from-pink-500 to-rose-600',
      skillCount: 8
    },
    {
      id: 'data',
      name: 'Data Science',
      description: 'Analytics and machine learning',
      icon: <Database className="w-5 h-5" />,
      color: 'from-green-500 to-emerald-600',
      skillCount: 12
    },
    {
      id: 'business',
      name: 'Business',
      description: 'Management and strategy',
      icon: <Briefcase className="w-5 h-5" />,
      color: 'from-orange-500 to-amber-600',
      skillCount: 7
    }
  ];

  const skills: Skill[] = [
    {
      id: '1',
      name: 'React.js',
      category: 'programming',
      level: 'advanced',
      progress: 85,
      totalCourses: 12,
      completedCourses: 10,
      avgRating: 4.8,
      studyTime: 245,
      lastPracticed: '2024-01-29',
      trending: true,
      inDemand: true,
      estimatedLearningTime: '3-4 months',
      relatedSkills: ['JavaScript', 'Redux', 'Next.js'],
      certificationAvailable: true,
      marketDemand: 'very-high'
    },
    {
      id: '2',
      name: 'Python',
      category: 'programming',
      level: 'intermediate',
      progress: 72,
      totalCourses: 15,
      completedCourses: 11,
      avgRating: 4.7,
      studyTime: 180,
      lastPracticed: '2024-01-28',
      trending: true,
      inDemand: true,
      estimatedLearningTime: '4-5 months',
      prerequisites: ['Basic Programming'],
      relatedSkills: ['Django', 'Flask', 'Data Analysis'],
      certificationAvailable: true,
      marketDemand: 'very-high'
    },
    {
      id: '3',
      name: 'Machine Learning',
      category: 'data',
      level: 'intermediate',
      progress: 58,
      totalCourses: 20,
      completedCourses: 12,
      avgRating: 4.6,
      studyTime: 320,
      lastPracticed: '2024-01-25',
      trending: true,
      inDemand: true,
      estimatedLearningTime: '6-8 months',
      prerequisites: ['Python', 'Statistics', 'Mathematics'],
      relatedSkills: ['Deep Learning', 'TensorFlow', 'Scikit-learn'],
      certificationAvailable: true,
      marketDemand: 'very-high'
    },
    {
      id: '4',
      name: 'UI/UX Design',
      category: 'design',
      level: 'intermediate',
      progress: 68,
      totalCourses: 8,
      completedCourses: 5,
      avgRating: 4.5,
      studyTime: 150,
      lastPracticed: '2024-01-30',
      trending: false,
      inDemand: true,
      estimatedLearningTime: '4-6 months',
      relatedSkills: ['Figma', 'Adobe XD', 'Prototyping'],
      certificationAvailable: true,
      marketDemand: 'high'
    },
    {
      id: '5',
      name: 'Project Management',
      category: 'business',
      level: 'beginner',
      progress: 35,
      totalCourses: 6,
      completedCourses: 2,
      avgRating: 4.3,
      studyTime: 45,
      lastPracticed: '2024-01-20',
      trending: false,
      inDemand: true,
      estimatedLearningTime: '2-3 months',
      relatedSkills: ['Agile', 'Scrum', 'Leadership'],
      certificationAvailable: true,
      marketDemand: 'high'
    },
    {
      id: '6',
      name: 'Node.js',
      category: 'programming',
      level: 'intermediate',
      progress: 64,
      totalCourses: 10,
      completedCourses: 6,
      avgRating: 4.6,
      studyTime: 120,
      lastPracticed: '2024-01-27',
      trending: true,
      inDemand: true,
      estimatedLearningTime: '3-4 months',
      prerequisites: ['JavaScript'],
      relatedSkills: ['Express.js', 'MongoDB', 'API Development'],
      certificationAvailable: true,
      marketDemand: 'very-high'
    }
  ];

  const learningPaths: LearningPath[] = [
    {
      id: '1',
      title: 'Full Stack Developer',
      description: 'Become a complete web developer',
      skills: ['React.js', 'Node.js', 'MongoDB', 'Express.js'],
      duration: '8-12 months',
      difficulty: 'intermediate',
      completionRate: 78
    },
    {
      id: '2',
      title: 'Data Scientist',
      description: 'Master data analysis and ML',
      skills: ['Python', 'Machine Learning', 'Statistics', 'SQL'],
      duration: '10-14 months',
      difficulty: 'advanced',
      completionRate: 65
    },
    {
      id: '3',
      title: 'Product Manager',
      description: 'Lead product development',
      skills: ['Project Management', 'Analytics', 'Strategy', 'Leadership'],
      duration: '6-8 months',
      difficulty: 'intermediate',
      completionRate: 85
    }
  ];

  const getMarketDemandColor = (demand: string) => {
    switch (demand) {
      case 'very-high': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'from-green-500 to-emerald-600';
      case 'intermediate': return 'from-yellow-500 to-orange-600';
      case 'advanced': return 'from-red-500 to-pink-600';
      case 'expert': return 'from-purple-500 to-indigo-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const filteredSkills = skills.filter(skill => {
    const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || skill.level === selectedLevel;
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         skill.relatedSkills.some(related => related.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesLevel && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'progress': return b.progress - a.progress;
      case 'name': return a.name.localeCompare(b.name);
      case 'demand': return a.marketDemand.localeCompare(b.marketDemand);
      case 'trending': return Number(b.trending) - Number(a.trending);
      default: return 0;
    }
  });

  const skillsOverview = {
    totalSkills: skills.length,
    completedSkills: skills.filter(s => s.progress >= 80).length,
    inProgress: skills.filter(s => s.progress > 0 && s.progress < 80).length,
    avgProgress: Math.round(skills.reduce((acc, skill) => acc + skill.progress, 0) / skills.length)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Brain className="w-7 h-7" />
              Skills Portfolio
            </h1>
            <p className="text-indigo-100">
              Track your learning progress and discover new opportunities
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAssessment(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors"
            >
              <Target className="w-4 h-4" />
              Skill Assessment
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium">
              <Plus className="w-4 h-4" />
              Add Skill
            </button>
          </div>
        </div>

        {/* Skills Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Brain className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">{skillsOverview.totalSkills}</p>
            <p className="text-xs text-indigo-100">Total Skills</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">{skillsOverview.completedSkills}</p>
            <p className="text-xs text-indigo-100">Mastered</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Activity className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">{skillsOverview.inProgress}</p>
            <p className="text-xs text-indigo-100">In Progress</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-indigo-200" />
            <p className="text-2xl font-bold">{skillsOverview.avgProgress}%</p>
            <p className="text-xs text-indigo-100">Avg Progress</p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Skill Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`p-4 rounded-lg border-2 text-center transition-all ${
                selectedCategory === category.id
                  ? 'border-brand-indigo-500 bg-brand-indigo-50'
                  : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
              }`}
            >
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${category.color} text-white mx-auto mb-3 flex items-center justify-center`}>
                {category.icon}
              </div>
              <h3 className="font-medium text-gray-900 mb-1">{category.name}</h3>
              <p className="text-xs text-gray-600 mb-2">{category.description}</p>
              <span className="text-sm font-medium text-indigo-600">{category.skillCount} skills</span>
            </button>
          ))}
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col md:flex-row gap-4 flex-1">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search skills..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500"
              />
            </div>
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500"
            >
              <option value="all">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-indigo-500"
            >
              <option value="progress">Sort by Progress</option>
              <option value="name">Sort by Name</option>
              <option value="demand">Sort by Demand</option>
              <option value="trending">Sort by Trending</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Skills Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill) => (
            <div key={skill.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
              <div className={`h-2 bg-gradient-to-r ${getLevelColor(skill.level)}`}></div>
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{skill.name}</h3>
                      {skill.trending && <Sparkles className="w-4 h-4 text-yellow-500" />}
                      {skill.inDemand && <TrendingUp className="w-4 h-4 text-green-500" />}
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                        skill.level === 'beginner' ? 'bg-green-100 text-green-700' :
                        skill.level === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                        skill.level === 'advanced' ? 'bg-red-100 text-red-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {skill.level}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getMarketDemandColor(skill.marketDemand)}`}>
                        {skill.marketDemand.replace('-', ' ')} demand
                      </span>
                    </div>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <Bookmark className="w-4 h-4" />
                  </button>
                </div>

                {/* Progress */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Progress</span>
                    <span className="font-medium">{skill.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full bg-gradient-to-r ${getLevelColor(skill.level)} transition-all`}
                      style={{ width: `${skill.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <BookOpen className="w-4 h-4 text-gray-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Courses</p>
                    <p className="font-medium">{skill.completedCourses}/{skill.totalCourses}</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <Clock className="w-4 h-4 text-gray-600 mx-auto mb-1" />
                    <p className="text-xs text-gray-600">Study Time</p>
                    <p className="font-medium">{skill.studyTime}h</p>
                  </div>
                </div>

                {/* Related Skills */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Related Skills</p>
                  <div className="flex flex-wrap gap-1">
                    {skill.relatedSkills.slice(0, 3).map((related) => (
                      <span key={related} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded">
                        {related}
                      </span>
                    ))}
                    {skill.relatedSkills.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        +{skill.relatedSkills.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedSkill(skill.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 text-sm font-medium"
                  >
                    <Play className="w-4 h-4" />
                    Continue
                  </button>
                  <button className="px-3 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">
                    Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="divide-y divide-gray-200">
            {filteredSkills.map((skill) => (
              <div key={skill.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${getLevelColor(skill.level)} flex items-center justify-center text-white font-bold`}>
                      {skill.name.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{skill.name}</h3>
                        {skill.trending && <Sparkles className="w-4 h-4 text-yellow-500" />}
                        {skill.certificationAvailable && <Award className="w-4 h-4 text-blue-500" />}
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getMarketDemandColor(skill.marketDemand)}`}>
                          {skill.marketDemand.replace('-', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="capitalize">{skill.level} level</span>
                        <span>{skill.completedCourses}/{skill.totalCourses} courses completed</span>
                        <span>{skill.studyTime} hours practiced</span>
                        <span>Last practiced: {new Date(skill.lastPracticed).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="w-16 h-16 relative">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32" cy="32" r="28"
                            stroke="currentColor" strokeWidth="4" fill="none"
                            className="text-gray-200"
                          />
                          <circle
                            cx="32" cy="32" r="28"
                            stroke="currentColor" strokeWidth="4" fill="none"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - skill.progress / 100)}`}
                            className="text-indigo-600 transition-all"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-900">{skill.progress}%</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <Heart className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedSkill(skill.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 font-medium"
                      >
                        Continue
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Learning Paths Recommendation */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Recommended Learning Paths</h2>
          <button className="text-brand-indigo-600 hover:text-brand-indigo-700 font-medium text-sm">
            View All Paths
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {learningPaths.map((path) => (
            <div key={path.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-semibold text-gray-900 mb-2">{path.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{path.description}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {path.skills.slice(0, 3).map((skill) => (
                  <span key={skill} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                    {skill}
                  </span>
                ))}
                {path.skills.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                    +{path.skills.length - 3} more
                  </span>
                )}
              </div>
              <div className="flex justify-between text-sm text-gray-600 mb-3">
                <span>{path.duration}</span>
                <span className="capitalize">{path.difficulty}</span>
              </div>
              <button className="w-full px-3 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700 text-sm font-medium">
                Start Learning Path
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Skill Assessment Modal */}
      {showAssessment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">Skill Assessment</h3>
                <button
                  onClick={() => setShowAssessment(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <Target className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Discover Your Skills</h4>
                <p className="text-gray-600">
                  Take a comprehensive assessment to identify your current skill levels and get personalized recommendations.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4 text-center">
                  <Code className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h5 className="font-medium text-gray-900">Technical Skills</h5>
                  <p className="text-sm text-gray-600">Programming, tools, and frameworks</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 text-center">
                  <Users className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h5 className="font-medium text-gray-900">Soft Skills</h5>
                  <p className="text-sm text-gray-600">Communication, leadership, and teamwork</p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowAssessment(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button className="px-6 py-2 bg-brand-indigo-600 text-white rounded-lg hover:bg-brand-indigo-700">
                  Start Assessment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}